import type { DayHours } from '../wire';
import type { Row } from './registry';
import { wallTime, weekdayName } from '../ui/time';

/**
 * The tenant's week, turned into the rows the `hours` block draws.
 *
 * ## Why this exists at all
 *
 * `/api/site` now projects `tenant_config.hours` — the row the booking engine
 * itself reads (spec §6.4.3 lists hours among the content blocks). Before that,
 * the block could only render what an owner had *typed* into config, so a shop
 * that changed its Sunday in the one place that decides what is bookable went
 * on advertising the old one. A page disagreeing with the book it sits on is
 * the failure worth fixing; two places to edit the same fact was the cause.
 *
 * ## Why it groups
 *
 * A seven-row table is a serialization, not a sign. Every real door says
 * "Monday – Friday 8:00 AM – 8:00 PM", so consecutive days with identical hours
 * collapse into one row — and only *consecutive* ones, because "Monday,
 * Wednesday, Friday" printed as a range would be a lie about Tuesday.
 *
 * Pure and total: seven days in, at most seven rows out, no clock and no
 * network. The formatting it borrows (`weekdayName`, `wallTime`) is the same
 * module every other time on this page goes through, which is what keeps a
 * component from reaching for `toLocaleTimeString()` on a tired afternoon.
 */
export function hoursRows(hours: readonly DayHours[]): Row[] {
  if (hours.length === 0) return [];

  const days = [...hours].sort((a, b) => a.weekday - b.weekday);
  const rows: Row[] = [];
  let run: DayHours[] = [];

  const flush = (): void => {
    const first = run[0];
    const last = run[run.length - 1];
    if (first === undefined || last === undefined) return;
    rows.push({ label: labelFor(first, last), value: valueFor(first) });
    run = [];
  };

  for (const day of days) {
    const previous = run[run.length - 1];
    // A break in the run when the days are not adjacent, or when the hours
    // differ. Adjacency is checked first because it is the cheaper comparison
    // and the one a duplicate weekday in a hand-written row would trip.
    if (previous !== undefined && (day.weekday !== previous.weekday + 1 || !same(day, previous))) {
      flush();
    }
    run.push(day);
  }
  flush();

  return rows;
}

/** `Monday`, or `Monday – Friday` for a run of two or more. */
function labelFor(first: DayHours, last: DayHours): string {
  const from = weekdayName(first.weekday);
  return first.weekday === last.weekday ? from : `${from} – ${weekdayName(last.weekday)}`;
}

/**
 * `8:00 AM – 8:00 PM`, or `11:30 AM – 2:00 PM, 5:30 PM – 10:00 PM` for a
 * restaurant's split shift (spec §4.6) — both windows, never the envelope
 * around them, because "11:30 AM – 10:00 PM" sends somebody to a locked door at
 * four o'clock.
 */
function valueFor(day: DayHours): string {
  if (day.closed || day.windows.length === 0) return 'Closed';
  return day.windows.map((w) => `${wallTime(w.start)} – ${wallTime(w.end)}`).join(', ');
}

function same(a: DayHours, b: DayHours): boolean {
  return valueFor(a) === valueFor(b);
}
